import React, { useEffect, useState, useContext } from 'react';
import {
  QueryProvider,
  extend, hoist, QueryContext
} from '@thd-nucleus/data-sources';
import { BackToTop } from '@thd-olt-component-react/back-to-top';
import { ErrorBoundary } from '@thd-olt-component-react/error-boundary';
import { Col, Row } from '@thd-olt-component-react/grid';
import { ProductHotspots } from '@thd-olt-component-react/product-hotspots';
import { Typography } from '@one-thd/sui-atomic-components';
import { CustomProductPodDetails, ProductPodGroup } from '@thd-olt-component-react/product-pod-group';
import { Sharebelt, ShareBeltPrint, ShareBeltShareButton } from '@thd-olt-component-react/thd-sharebelt';
import { DynamicRecsWrapper } from '@thd-olt-component-react/thd-recs-containers';
import { ShopByRoomPod } from './ShopByRoomPod';
import { CurationDesktop } from '../curation/CurationDesktop';
import { SimilarCollections } from '../similar-collections/similar-collections';
import { ShopByRoomDataModel } from './ShopByRoomDataModel';
import { propTypes, defaultProps } from './ShopByRoomPropTypes';
import './shop-by-room.scss';

const HoistedProductPodGroup = hoist(ProductPodGroup, ShopByRoomPod);

export const ShopByRoomCollectionDesktop = ({
  collection,
  preview,
  spaceCollectionId,
  skipFn,
  storeId
}) => {
  const {
    collectionId,
    curations,
    metadata,
    products,
    visuallySimilarItems
  } = collection || {};
  const { defaultVariables } = useContext(QueryContext);
  const {
    name, collectionDescription, roomType, primaryGalleryImage, roomStyle
  } = metadata || {};
  const { images } = primaryGalleryImage || {};
  const [primaryImage] = images || [];
  const [showVisualRecs, setShowVisualRecs] = useState(false);
  const visItemIds = (visuallySimilarItems || []).map((items) => items.itemId).filter((itemId) => !!itemId);

  useEffect(() => {
    setShowVisualRecs(true);
  }, []);

  return (
    <Row className="shop-by-room">
      <Col>
        <ErrorBoundary name="Header">
          <div className="shop-by-room__header">
            <div data-testid="quickLinks" className="shop-by-room__quicklinks">
              {
                (roomStyle || []).filter((style) => style.label && style.url).map((quickLink, i) => (
                  <a key={`quick-${i}`} className="shop-by-room__quicklinks__item" href={quickLink.url}>
                    {quickLink.label}
                  </a>
                ))
              }
            </div>
            <div className="sui-mt-4 sui-mb-4">
              <Typography variant="h1" align="center">
                {name} <span className="sui-text-xs sui-ml-1">({products?.length})</span>
              </Typography>
            </div>
            <div className="shop-by-room__overview">
              {
                collectionDescription
              }
            </div>
          </div>
        </ErrorBoundary>
        <ErrorBoundary name="Media">
          <div className="shop-by-room__media">
            <Row>
              <Col fallback={3}>
                <div className="shop-by-room__media--sharebelt-wrapper">
                  <Sharebelt
                    className="collections__sharebelt"
                    collectionId={collectionId}
                    vertical
                  >
                    <ShareBeltShareButton
                      collectionImageSrc={primaryImage?.url}
                    />
                    <ShareBeltPrint />
                  </Sharebelt>
                </div>
              </Col>
              <Col fallback={6}>
                <ProductHotspots
                  collectionId={spaceCollectionId}
                  preview={preview}
                />
              </Col>
              <Col fallback={3} />
            </Row>
          </div>
        </ErrorBoundary>
        <ErrorBoundary name="Products">
          <Row className="shop-by-room__products">
            <QueryProvider
              dataSource="Collection"
              defaultVariables={{
                skipSubscribeAndSave: true,
                skipSpecificationGroup: true,
                storeId,
                skipKPF: true,
                ...(typeof defaultVariables?.current?.isBrandPricingPolicyCompliant === 'boolean'
                    && { isBrandPricingPolicyCompliant:
                      defaultVariables.current.isBrandPricingPolicyCompliant })
              }}
              skip={skipFn}
              cacheKey="shopbyroom-desktop"
              inherit="collections-page"
            >
              <HoistedProductPodGroup DetailsPod={CustomProductPodDetails} customScheme="visuallysimilar_1_0">
                {
                  (products || []).map((sku, itemIndex) => {
                    const position = itemIndex;
                    const { hasVisuallySimilar } = sku?.info;
                    return (
                      <Col
                        className="shop-by-room__products-pod"
                        key={`col-${sku.itemId}`}
                        data-testid="sbr-product"
                        md={2}
                        sm={3}
                        fallback={6}
                      >
                        <ShopByRoomPod
                          itemId={sku.itemId}
                          position={position}
                          showSimilarItem={hasVisuallySimilar}
                          storeId={storeId}
                          parent="PLP"
                        />
                      </Col>
                    );
                  })
                }
              </HoistedProductPodGroup>
            </QueryProvider>
          </Row>
        </ErrorBoundary>
        <Row className="shop-by-room__visually-similar">
          <Col>
            {showVisualRecs && visItemIds?.length !== 0 && (
              <QueryProvider
                cacheKey="visual-recs-multi"
                defaultVariables={{
                  ...(
                    typeof defaultVariables?.current?.isBrandPricingPolicyCompliant === 'boolean'
                      && { isBrandPricingPolicyCompliant:
                        defaultVariables.current.isBrandPricingPolicyCompliant }
                  )
                }}
              >
                <DynamicRecsWrapper
                  schemaName="visuallysimilar_multi"
                  anchorId={visItemIds.join(',')}
                  hideFavorites={false}
                  hideSwatches={false}
                  errorBoundary
                  serviceType="mutlianchor"
                  hydrator={{
                    id: 'visuallysimilar_multi_recs',
                    delay: 1500,
                    scrollBuffer: 750
                  }}
                />
              </QueryProvider>
            )}
          </Col>
        </Row>

        <ErrorBoundary name="You May Like">
          {
            (curations || []).map((curationId) => (
              <div className="shop-by-room__curations" key={`curation-${curationId}`}>
                <CurationDesktop curationId={curationId} preview={preview} />
              </div>
            ))
          }
        </ErrorBoundary>
        <ErrorBoundary name="Similar Collections">
          <Row>
            <Col>
              <SimilarCollections room={roomType} collectionId={spaceCollectionId} />
            </Col>
          </Row>
        </ErrorBoundary>
        <BackToTop />
      </Col>
    </Row>
  );
};

ShopByRoomCollectionDesktop.displayName = 'ShopByRoomCollectionDesktop';

ShopByRoomCollectionDesktop.dataModel = extend({},
  ShopByRoomDataModel,
  DynamicRecsWrapper,
  ProductPodGroup,
);

ShopByRoomCollectionDesktop.propTypes = propTypes;

ShopByRoomCollectionDesktop.defaultProps = defaultProps;